import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatCard, Button } from "@repo/o37-ui";
import { CreditCard, Receipt, TrendingUp, Bell, Sparkles, BarChart3 } from "lucide-react";
import { formatCents, getMonthlyEquivalent, getAnnualEquivalent, HoldingsBillingCycle, HoldingsAlertCounts, HoldingsAlert, HoldingsCompany } from '../../types';
import SubscriptionList from './SubscriptionList';
import OneTimeCostList from './OneTimeCostList';
import SubscriptionPatternModal from '../../components/Holdings/SubscriptionPatternModal';

const API_URL = import.meta.env.DEV ? "http://localhost:3001/api" : "/api";

type Tab = 'subscriptions' | 'one-time';

interface CostsSummary {
  totalSubscriptions: number;
  activeSubscriptions: number;
  subscriptionMonthlyRate: number;
  subscriptionAnnualRate: number;
  compensationMonthlyRate: number;
  compensationAnnualRate: number;
  activeCompensations: number;
  monthlyRunRate: number;
  annualRunRate: number;
  totalOneTimeCosts: number;
  oneTimeCostsThisMonth: number;
  oneTimeCostsThisYear: number;
}

export default function CostsDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('subscriptions');
  const [summary, setSummary] = useState<CostsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertCounts, setAlertCounts] = useState<HoldingsAlertCounts | null>(null);
  const [subscriptionAlerts, setSubscriptionAlerts] = useState<Map<string, HoldingsAlert[]>>(new Map());
  const [companies, setCompanies] = useState<HoldingsCompany[]>([]);
  const [showPatternModal, setShowPatternModal] = useState(false);

  const userId = localStorage.getItem('app_user_id') || '';
  const userRole = localStorage.getItem('app_role') || 'user';

  useEffect(() => {
    loadSummary();
    loadAlerts();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_URL}/holdings/companies`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.map((c: any) => ({
          id: c.id,
          name: c.name,
          status: c.status
        })));
      }
    } catch (e) {
      console.error('Failed to load companies:', e);
    }
  };

  const loadSummary = async () => {
    try {
      // Load subscriptions, one-time costs, and compensation in parallel
      const [subsResponse, costsResponse, compResponse] = await Promise.all([
        fetch(`${API_URL}/holdings/subscriptions`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/one-time-costs`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        }),
        fetch(`${API_URL}/holdings/compensation?status=active`, {
          headers: { 'x-user-id': userId, 'x-user-role': userRole }
        })
      ]);

      if (subsResponse.ok && costsResponse.ok) {
        const subscriptions = await subsResponse.json();
        const oneTimeCosts = await costsResponse.json();
        const compensations = compResponse.ok ? await compResponse.json() : [];

        // Calculate run rates from active subscriptions
        const activeSubscriptions = subscriptions.filter((s: any) => s.status === 'active');
        const subscriptionMonthlyRate = activeSubscriptions.reduce((sum: number, s: any) => {
          return sum + getMonthlyEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
        }, 0);
        const subscriptionAnnualRate = activeSubscriptions.reduce((sum: number, s: any) => {
          return sum + getAnnualEquivalent(s.cost_cents, s.billing_cycle as HoldingsBillingCycle);
        }, 0);

        // Calculate compensation run rates (annual / 12 = monthly)
        const compensationAnnualRate = compensations.reduce((sum: number, c: any) => {
          return sum + (c.total_annual_cost_cents || 0);
        }, 0);
        const compensationMonthlyRate = Math.round(compensationAnnualRate / 12);

        // Calculate one-time costs for current month and year
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const startOfYear = new Date(now.getFullYear(), 0, 1);

        const oneTimeCostsThisMonth = oneTimeCosts
          .filter((c: any) => new Date(c.date) >= startOfMonth)
          .reduce((sum: number, c: any) => sum + c.amount_cents, 0);

        const oneTimeCostsThisYear = oneTimeCosts
          .filter((c: any) => new Date(c.date) >= startOfYear)
          .reduce((sum: number, c: any) => sum + c.amount_cents, 0);

        setSummary({
          totalSubscriptions: subscriptions.length,
          activeSubscriptions: activeSubscriptions.length,
          subscriptionMonthlyRate,
          subscriptionAnnualRate,
          compensationMonthlyRate,
          compensationAnnualRate,
          activeCompensations: compensations.length,
          monthlyRunRate: subscriptionMonthlyRate + compensationMonthlyRate,
          annualRunRate: subscriptionAnnualRate + compensationAnnualRate,
          totalOneTimeCosts: oneTimeCosts.length,
          oneTimeCostsThisMonth,
          oneTimeCostsThisYear
        });
      }
    } catch (e) {
      console.error('Failed to load costs summary:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      // Load unresolved alert counts
      const countsResponse = await fetch(`${API_URL}/holdings/alerts/unresolved`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (countsResponse.ok) {
        const counts = await countsResponse.json();
        setAlertCounts(counts);
      }

      // Load all unresolved alerts to map to subscriptions
      const alertsResponse = await fetch(`${API_URL}/holdings/alerts?resolved=false`, {
        headers: { 'x-user-id': userId, 'x-user-role': userRole }
      });

      if (alertsResponse.ok) {
        const alerts = await alertsResponse.json();
        // Group alerts by subscription ID
        const alertsBySubscription = new Map<string, HoldingsAlert[]>();
        for (const alert of alerts) {
          if (alert.subscription_id) {
            const existing = alertsBySubscription.get(alert.subscription_id) || [];
            existing.push({
              id: alert.id,
              alertType: alert.alert_type,
              severity: alert.severity,
              title: alert.title,
              description: alert.description,
              subscriptionId: alert.subscription_id,
              transactionId: alert.transaction_id,
              expectedAmountCents: alert.expected_amount_cents,
              actualAmountCents: alert.actual_amount_cents,
              isResolved: alert.is_resolved,
              resolvedBy: alert.resolved_by,
              resolvedAt: alert.resolved_at,
              resolutionAction: alert.resolution_action,
              createdAt: alert.created_at
            });
            alertsBySubscription.set(alert.subscription_id, existing);
          }
        }
        setSubscriptionAlerts(alertsBySubscription);
      }
    } catch (e) {
      console.error('Failed to load alerts:', e);
    }
  };

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'subscriptions', label: 'Subscriptions', icon: <CreditCard className="w-4 h-4" /> },
    { id: 'one-time', label: 'One-Time Costs', icon: <Receipt className="w-4 h-4" /> }
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Cost Tracking</h1>
          <p className="text-neutral-500">Manage subscriptions and track expenses</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/holdings/analytics/expected-vs-actual')}
            icon={<BarChart3 className="w-4 h-4" />}
            text="Expected vs Actual"
          />
          <Button
            variant="outline"
            onClick={() => setShowPatternModal(true)}
            icon={<Sparkles className="w-4 h-4" />}
            text="Detect Subscriptions"
          />
        </div>
      </div>

      {/* Alerts Banner */}
      {alertCounts && alertCounts.total > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bell className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <div className="font-medium text-amber-800">
                  {alertCounts.total} item{alertCounts.total !== 1 ? 's' : ''} need attention
                </div>
                <div className="text-sm text-amber-600 flex gap-3">
                  {alertCounts.price_change > 0 && (
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {alertCounts.price_change} price change{alertCounts.price_change !== 1 ? 's' : ''}
                    </span>
                  )}
                  {alertCounts.match_review > 0 && (
                    <span>{alertCounts.match_review} match review{alertCounts.match_review !== 1 ? 's' : ''}</span>
                  )}
                  {alertCounts.new_recurring > 0 && (
                    <span>{alertCounts.new_recurring} new recurring</span>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/holdings/bank-imports/review')}
              text="Review"
            />
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {!loading && summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            label="Monthly Run Rate"
            value={formatCents(summary.monthlyRunRate)}
            hint={`Subs: ${formatCents(summary.subscriptionMonthlyRate)} + Payroll: ${formatCents(summary.compensationMonthlyRate)}`}
          />
          <StatCard
            label="Annual Run Rate"
            value={formatCents(summary.annualRunRate)}
            hint={`${summary.activeSubscriptions} subs, ${summary.activeCompensations} payroll`}
          />
          <StatCard
            label="One-Time (Month)"
            value={formatCents(summary.oneTimeCostsThisMonth)}
            hint="This month"
          />
          <StatCard
            label="One-Time (YTD)"
            value={formatCents(summary.oneTimeCostsThisYear)}
            hint="Year to date"
          />
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-1 p-1 bg-neutral-100 rounded-lg w-fit mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white text-neutral-900 shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'subscriptions' && (
        <SubscriptionList
          onDataChange={loadSummary}
          subscriptionAlerts={subscriptionAlerts}
        />
      )}

      {activeTab === 'one-time' && (
        <OneTimeCostList onDataChange={loadSummary} />
      )}

      {/* Subscription Pattern Detection Modal */}
      <SubscriptionPatternModal
        isOpen={showPatternModal}
        onClose={() => setShowPatternModal(false)}
        onPatternsCreated={() => {
          setShowPatternModal(false);
          loadSummary();
        }}
        companies={companies}
      />
    </div>
  );
}
